import React, { useCallback, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronLeftLargeIcon from '@atlaskit/icon/core/chevron-left';
import { token } from '@atlaskit/tokens';
import Button, { LinkButton } from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { FlexContainer } from '@atlaskit/right-side-panel';

import type { OptionData } from '@confluence/user-and-group-search/entry-points/user-picker.ts';
import { useIsProductAdmin } from '@confluence/current-user/entry-points/useIsProductAdmin.ts';
import {
	SitePermissionTypeFilter,
	SitePermissionType,
} from '@confluence/user-and-group-search/entry-points/UserAndGroupSearchQuery.ts';
import { UserAndGroupSearchPicker } from '@confluence/user-and-group-search/entry-points/UserAndGroupSearchPicker.ts';
import { ADMIN_SITE_SPACE_PERMISSIONS } from '@confluence/named-routes/entry-points/routes.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { FeedbackCollectorButton } from '@confluence/space-roles/entry-points/FeedbackCollector.tsx';
import type { SpaceRole } from '@confluence/space-roles/entry-points/space-role-types.ts';
import {
	useAccessClassInclusion,
	useAccessClassTransformer,
} from '@confluence/space-roles/entry-points/TableRow.tsx';
import { fg } from '@confluence/feature-gating';
import { useTeamPicker } from '@confluence/permissions-principals/entry-points/useTeamPicker.ts';

import {
	SpaceSelectionRadioButtons,
	SpaceSelection,
} from './page-components/SpaceSelectionRadioButtons.tsx';
import {
	ActionSelectionRadioButtons,
	ActionSelection,
} from './page-components/ActionSelectionRadioButtons.tsx';
import {
	BulkRoleAssignmentSpaceType,
	RoleAssignmentPrincipalType,
} from './graphql/__types__/BulkSetRoleAssignmentToSpacesMutation.ts';
import { RoleSelectionSection } from './page-components/RoleSelectionSection.tsx';
import { RestrictedView } from './restricted/RestrictedView.tsx';
// eslint-disable-next-line import/no-cycle
import { BulkAccessModal } from './bulk-access-dialogs/BulkAccessModal.tsx';
import { useBulkRoleSetAssignment } from './useBulkRoleSetAssignment.tsx';

export const ANALYTICS_SOURCE = 'bulkAccessManagementScreen';

const i18n = defineMessages({
	bulkRoleAccessHeader: {
		id: 'bulk-permissions.space.role-access-header',
		defaultMessage: 'Manage space access in bulk',
		description: 'Header text of the bulk role access page',
	},
	bulkRoleAccessSubtext: {
		id: 'bulk-permissions.space.role-access-subtext',
		defaultMessage:
			'Quickly add, change, or remove access for individual users or groups across spaces on your Confluence site.',
		description: 'Subtext of the bulk role access page',
	},
	bulkRoleAccessTeamsSubtext: {
		id: 'bulk-permissions.space.role-access-subtext-teams',
		defaultMessage:
			'Quickly add, change, or remove access for individual users, teams, or groups across spaces on your Confluence site.',
		description: 'Subtext of the bulk role access page when teams are enabled',
	},
	warningSubtext: {
		id: 'bulk-permissions.space.warning-subtext',
		defaultMessage: '<b>This can’t be undone</b> so make sure to double check your selections.',
		description: 'Subtext of the bulk role access page that warns about the all or nothing action',
	},
	userPickerAccessHeader: {
		id: 'bulk-permissions.space.user-picker-access-header',
		defaultMessage: 'Who<asterisk>*</asterisk>',
		description:
			'Header text of the user picker for bulk access management, with an asterisk indicating a required field',
	},
	userPickerPlaceholder: {
		id: 'bulk-permissions.space.user-picker-placeholder',
		defaultMessage: 'Add a user, group, or user class',
		description: 'Placeholder text for the user picker',
	},
	userPickerTeamsPlaceholder: {
		id: 'bulk-permissions.space.user-picker-teams-placeholder',
		defaultMessage: 'Add a user, team, group, or user class',
		description: 'Placeholder text for the user picker which includes teams',
	},
	backToSpacePermissions: {
		id: 'bulk-permissions.space-permissions-link.role-access',
		defaultMessage: 'Space permissions',
		description: 'Link to a space permissions page',
	},
	userPickerSubtext: {
		id: 'bulk-permissions.space.user-picker-subtext.role-access',
		defaultMessage: 'You may only select one for this action.',
		description:
			'Subtext explaining that only a single user or group can be selected in the user picker field for this action',
	},
	applyButtonTextAdd: {
		id: 'bulk-permissions.space.apply-button-text-add.role-access',
		defaultMessage: 'Add to all',
		description:
			'Text label for the button that applies the selected user or group role addition to all spaces',
	},
	applyButtonTextRemoveRolesOnly: {
		id: 'bulk-permissions.space.apply-button-text-remove-roles-only.role-access',
		defaultMessage: 'Remove access',
		description: 'Text for an apply button that remove user access',
	},
	requirementMessage: {
		id: 'bulk-permissions.space.requirement-message.role-access',
		defaultMessage: '<asterisk>* </asterisk> Required to perform action',
		description: 'Message that indicates a required field',
	},
});

const bulkAddRolesHeaderStyle = xcss({
	font: token('font.heading.large'),
	fontWeight: token('font.weight.medium'),
	paddingBottom: 'space.100',
	paddingTop: 'space.300',
});

const bulkRolesSubtextStyle = xcss({
	font: token('font.body'),
	width: '75%',
});

const principlePickerHeaderStyle = xcss({
	font: token('font.body.large'),
	fontWeight: token('font.weight.medium'),
	display: 'flex',
	flexDirection: 'row',
	paddingTop: 'space.300',
	paddingBottom: 'space.100',
});

const asteriskStyle = xcss({
	color: 'color.text.accent.red',
});

const principlePickerFineTextStyle = xcss({
	color: 'color.text.accent.gray',
	marginTop: 'space.050',
	font: token('font.body.small'),
});

const contentStyle = xcss({
	marginLeft: 'space.1000',
	marginRight: 'space.1000',
	paddingBottom: 'space.1000',
});

const backAndFeedbackButtonsStyle = xcss({
	width: '100%',
	marginLeft: 'space.negative.200',
	paddingTop: 'space.300',
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
});

const principlePickerStyle = xcss({
	paddingBottom: 'space.050',
});

const requirementTextStyle = xcss({
	font: token('font.body'),
	paddingTop: 'space.300',
	paddingBottom: 'space.1000',
	marginBottom: 'space.1000',
});

const styles = cssMap({
	removalWarningSubtext: {
		paddingBottom: token('space.300'),
	},
});

export const getPrincipalId = (principal: PrincipalOption) => {
	if (principal?.type === 'group') {
		return fg('nonsmart_user_picker_deprecation') ? principal?.id : principal?.extra?.id;
	}

	return principal?.id;
};

export const pickerResultsFilter = (options: OptionData[]) => {
	// TEMPORARY WORKAROUND https://product-fabric.atlassian.net/browse/CPE-3651:
	// Smart User Picker does not currently expose a reliable field to identify guest users.
	// We're using the presence of the "GUEST" lozenge as a proxy to filter out guest users.
	// This should be revisited once the platform team adds a proper field
	// to accurately indicate guest status.
	return options.filter((option) => {
		const { lozenge } = option;

		const isGuest =
			typeof lozenge === 'object' &&
			lozenge !== null &&
			'text' in lozenge &&
			lozenge.text === 'GUEST';

		return !isGuest;
	});
};

export interface PrincipalOption extends OptionData {
	extra?: any;
}

export const BulkAccessManagementPage = withFlags(
	withErrorBoundary({
		attribution: Attribution.PERMISSIONS_EXPERIENCE,
	})(({ flags }: { flags: FlagsStateContainer }) => {
		const { formatMessage } = useIntl();
		const { edition } = useSessionData();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { isProductAdmin, isAdminCheckLoading } = useIsProductAdmin();
		const accessClassInclusion = useAccessClassInclusion();
		const { pickerProps: teamPickerProps } = useTeamPicker();

		const [showDialog, setShowDialog] = useState(false);
		const [stagedPrincipal, setStagedPrincipal] = useState<PrincipalOption | undefined>(undefined);
		const [spaceSelection, setSpaceSelection] = useState<SpaceSelection>(
			SpaceSelection.AllExcludingPersonal,
		);
		const { setSearchQuery, transformOptions } = useAccessClassTransformer();

		const [actionSelection, setActionSelection] = useState<ActionSelection>(
			ActionSelection.AddOrChangeAccess,
		);

		const [roleAssignment, setRoleAssignment] = useState<SpaceRole | null>(null);

		const isRemoval = actionSelection === ActionSelection.RemoveAccess;

		const closeDialog = useCallback(() => {
			setShowDialog(false);
		}, [setShowDialog]);

		const { bulkSetRoleAssignmentToSpaces, bulkRemoveRoleAssignment } = useBulkRoleSetAssignment({
			closeDialog,
			flags,
			rolePermissions: roleAssignment?.permissions || [],
		});

		const getSpaceTypes = (targetValue) => {
			const spaceTypesArray = [
				BulkRoleAssignmentSpaceType.PERSONAL,
				BulkRoleAssignmentSpaceType.COLLABORATION,
				BulkRoleAssignmentSpaceType.GLOBAL,
				BulkRoleAssignmentSpaceType.KNOWLEDGE_BASE,
			];
			if (targetValue === SpaceSelection.AllExcludingPersonal) {
				return spaceTypesArray.filter((type) => type !== BulkRoleAssignmentSpaceType.PERSONAL);
			} else if (targetValue === SpaceSelection.AllIncludingPersonal) {
				return spaceTypesArray;
			} else if (targetValue === SpaceSelection.OnlyPersonal) {
				return [BulkRoleAssignmentSpaceType.PERSONAL];
			}
		};

		const getPrincipalType = (principalType: OptionData['type']) => {
			if (principalType === 'group') {
				return RoleAssignmentPrincipalType.GROUP;
			} else if (principalType === 'team') {
				return RoleAssignmentPrincipalType.GROUP;
			} else if (principalType === 'custom') {
				return RoleAssignmentPrincipalType.ACCESS_CLASS;
			} else {
				return RoleAssignmentPrincipalType.USER;
			}
		};

		const onDialogConfirm = useCallback(async () => {
			closeDialog();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId: 'bulk-access-management-confirm',
					source: ANALYTICS_SOURCE,
					attributes: {
						principalType:
							stagedPrincipal?.type === 'group'
								? stagedPrincipal?.extra?.usageType
								: stagedPrincipal?.type,
						verified: stagedPrincipal?.type === 'team' ? stagedPrincipal?.verified : 'N/A',
						selectedRole: roleAssignment?.name,
						spaceTypes: getSpaceTypes(spaceSelection)!,
						action: isRemoval ? 'remove' : 'add',
					},
				},
			}).fire();

			isRemoval
				? await bulkRemoveRoleAssignment({
						variables: {
							principalId: getPrincipalId(stagedPrincipal!),
							principalType: getPrincipalType(stagedPrincipal!.type),
							spaceTypes: getSpaceTypes(spaceSelection)!,
						},
					})
				: await bulkSetRoleAssignmentToSpaces({
						variables: {
							principalId: getPrincipalId(stagedPrincipal!),
							principalType: getPrincipalType(stagedPrincipal!.type),
							roleId: roleAssignment!.id,
							spaceTypes: getSpaceTypes(spaceSelection)!,
						},
					});
		}, [
			closeDialog,
			isRemoval,
			bulkRemoveRoleAssignment,
			stagedPrincipal,
			spaceSelection,
			bulkSetRoleAssignmentToSpaces,
			roleAssignment,
			createAnalyticsEvent,
		]);

		const handleBackToDefaultSpaceAccessClick = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId: 'defaultspaceperms',
					source: ANALYTICS_SOURCE,
				},
			}).fire();
		}, [createAnalyticsEvent]);

		const onSpaceTypeRadioChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				setSpaceSelection(e.target.value as SpaceSelection);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						actionSubject: 'radio',
						action: 'selected',
						actionSubjectId: 'bulkAccessManagementSpace',
						source: ANALYTICS_SOURCE,
						attributes: {
							spaceSelection: e.target.value,
							isRBACEnabled: true,
						},
					},
				}).fire();
			},
			[createAnalyticsEvent],
		);

		const onActionRadioChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				setActionSelection(e.target.value as ActionSelection);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						actionSubject: 'radio',
						action: 'selected',
						actionSubjectId: 'bulkAccessManagementAction',
						source: ANALYTICS_SOURCE,
						attributes: {
							actionSelection: e.target.value,
							isRBACEnabled: true,
						},
					},
				}).fire();
			},
			[createAnalyticsEvent],
		);

		//Only passing roleId to the analytics event to avoid leaking UGC
		//Ticket to audit rest of analytics events: https://product-fabric.atlassian.net/browse/RBAC-1552
		const onRoleChange = useCallback(
			(role: SpaceRole) => {
				setRoleAssignment(role);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						actionSubject: 'role picker',
						action: 'selected',
						actionSubjectId: 'bulkAccessManagement',
						source: ANALYTICS_SOURCE,
						attributes: {
							roleAssignment: role.id,
						},
					},
				}).fire();
			},
			[createAnalyticsEvent],
		);

		const onPrincipalChange = useCallback(
			(received: OptionData | undefined) => {
				// Only send an analytic event if a principal isn't deselected
				if (received) {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							actionSubject: 'principal',
							action: 'selected',
							actionSubjectId: 'bulkAccessManagementPrincipal',
							source: ANALYTICS_SOURCE,
							attributes: {
								principal: received.type,
								verified: received.type === 'team' ? received.verified : 'N/A',
								isRBACEnabled: true,
							},
						},
					}).fire();
				}
				setStagedPrincipal(received);
			},
			[createAnalyticsEvent],
		);

		const openDialog = useCallback(() => {
			setShowDialog(true);
		}, [setShowDialog]);

		// Don't render the page until we know if the user is a product admin
		// to avoid a flicker of the restricted view.
		if (isAdminCheckLoading) {
			return null;
		}

		if (!isProductAdmin || edition === ConfluenceEdition.FREE) {
			return <RestrictedView />;
		}

		return (
			<>
				{/* this FlexContainer is wrapped around the entire page so that the space exclusion panel opens at the page level and not section level */}
				<FlexContainer id="SpaceSelectionPanel">
					<Box xcss={contentStyle}>
						<Box xcss={backAndFeedbackButtonsStyle}>
							<LinkButton
								appearance="subtle"
								onClick={handleBackToDefaultSpaceAccessClick}
								href={ADMIN_SITE_SPACE_PERMISSIONS.toUrl({
									tab: 'allspaces',
								})}
								iconBefore={(iconProps) => <ChevronLeftLargeIcon {...iconProps} size="small" />}
							>
								{formatMessage(i18n.backToSpacePermissions)}
							</LinkButton>
							<FeedbackCollectorButton source="Bulk Space Roles" />
						</Box>

						<Box xcss={bulkAddRolesHeaderStyle}>{formatMessage(i18n.bulkRoleAccessHeader)}</Box>
						<Box xcss={bulkRolesSubtextStyle}>
							{formatMessage(
								teamPickerProps.includeTeamsUpdates
									? i18n.bulkRoleAccessTeamsSubtext
									: i18n.bulkRoleAccessSubtext,
							)}
						</Box>

						<Box xcss={principlePickerHeaderStyle}>
							{formatMessage(i18n.userPickerAccessHeader, {
								asterisk: (chunks: React.ReactNode[]) => <Box xcss={asteriskStyle}>{chunks}</Box>,
							})}
						</Box>
						<Box xcss={principlePickerStyle}>
							<UserAndGroupSearchPicker
								ariaLabel={formatMessage(i18n.userPickerPlaceholder)}
								data-test-id="user-and-group-search"
								fieldId="bulk-permissions-user-picker"
								includeGroup
								withUsers
								isMulti={false}
								smart={fg('nonsmart_user_picker_deprecation')}
								customInclusion={accessClassInclusion}
								placeholder={formatMessage(
									teamPickerProps.includeTeamsUpdates
										? i18n.userPickerTeamsPlaceholder
										: i18n.userPickerPlaceholder,
								)}
								sitePermissionTypeFilter={SitePermissionTypeFilter.NONE}
								// @ts-ignore - Type 'null' is not assignable to type 'string | ((input: { inputValue: string; }) => string | null)'
								// Using `ts-ignore` and not `ts-expect-error` here as TypeScript will fail on this line without `ts-ignore` but with `ts-expect-error`
								// This error was introduced after upgrading to TypeScript 5
								noOptionsMessage={null}
								onChange={(received) => {
									onPrincipalChange(received as OptionData);
								}}
								value={stagedPrincipal || []}
								filterSearchResults={(value) =>
									value?.extra?.permissionType !== SitePermissionType.EXTERNAL
								}
								onInputChange={(searchQuery) => setSearchQuery(searchQuery || '')}
								transformOptions={transformOptions}
								smartFilterOptions={pickerResultsFilter}
								includeTeams={teamPickerProps.includeTeams}
								includeTeamsUpdates={teamPickerProps.includeTeamsUpdates}
								verifiedTeams={teamPickerProps.verifiedTeams}
							/>
						</Box>
						<Box xcss={principlePickerFineTextStyle}>{formatMessage(i18n.userPickerSubtext)}</Box>

						<ActionSelectionRadioButtons
							actionSelection={actionSelection}
							onRadioChange={onActionRadioChange}
						/>

						<SpaceSelectionRadioButtons
							spaceSelection={spaceSelection}
							onRadioChange={onSpaceTypeRadioChange}
						/>

						{isRemoval ? (
							<Box xcss={styles.removalWarningSubtext}>
								{formatMessage(i18n.warningSubtext, {
									b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
								})}
							</Box>
						) : (
							<RoleSelectionSection
								onRoleChange={onRoleChange}
								roleAssignment={roleAssignment}
								analyticsSource={ANALYTICS_SOURCE}
								flags={flags}
							/>
						)}

						<Button
							appearance={isRemoval ? 'danger' : 'primary'}
							onClick={() => openDialog()}
							isDisabled={stagedPrincipal === undefined || (!isRemoval && roleAssignment === null)}
						>
							{formatMessage(
								isRemoval ? i18n.applyButtonTextRemoveRolesOnly : i18n.applyButtonTextAdd,
							)}
						</Button>

						<Inline xcss={requirementTextStyle}>
							{formatMessage(i18n.requirementMessage, {
								asterisk: (chunks: React.ReactNode[]) => <Box xcss={asteriskStyle}>{chunks}</Box>,
							})}
						</Inline>
					</Box>
					{showDialog && (
						<BulkAccessModal
							principalName={stagedPrincipal?.name || ''}
							isRemoval={isRemoval}
							spaceSelection={spaceSelection}
							isLoading={false}
							onConfirm={onDialogConfirm}
							onCancel={closeDialog}
							roleName={roleAssignment?.name}
						/>
					)}
				</FlexContainer>
			</>
		);
	}),
);
